/**
 * Core Question Evaluator
 *
 * Evaluates whether a question should be considered "core" based on dynamic conditions.
 * Uses the same condition grammar as Odyssey's visibleIf to determine core status.
 *
 * Core questions drive the equal-step progress calculation, while non-core questions
 * receive simulated progress interpolated between core milestones.
 */

import type { FormStepResult } from '../types'
import type { CoreIfCondition } from '../../types/form'

interface VisibleIfSingleCondition {
  formStepKey: string
  comparisonOperator: string
  expectedAnswer: unknown
}

interface VisibleIfMultiCondition {
  operator: string
  conditions: Array<string | VisibleIfSingleCondition | VisibleIfMultiCondition>
}

type EvaluableCondition = string | VisibleIfSingleCondition | VisibleIfMultiCondition

/**
 * Evaluates core-if conditions to determine if a question should be considered core.
 *
 * - `undefined` -> always core (no condition = unconditionally core)
 * - `false`     -> never core
 * - `true`      -> always core
 * - `string`    -> simple equality condition, e.g. `"interstitial = deepDive"`
 * - `object`    -> single or multi-condition (mirrors Odyssey's visibleIf shape)
 */
export const coreQuestionEvaluator = (formResults: FormStepResult) => {
  const getNestedValue = (path: string): unknown => {
    return path.split('.').reduce((current: unknown, key) => {
      if (current !== null && typeof current === 'object') {
        return (current as Record<string, unknown>)[key]
      }
      return undefined
    }, formResults as unknown)
  }

  const compareValues = (actual: unknown, operator: string, expected: unknown): boolean => {
    const actualNumber = Number(actual)
    const expectedNumber = Number(expected)
    const bothNumeric = !isNaN(actualNumber) && !isNaN(expectedNumber)

    switch (operator) {
      case '===':
        return actual === expected
      case '==':
        return actual == expected // intentional loose equality to mirror Odyssey's shouldShowQuestion
      case '!==':
        return actual !== expected
      case '!=':
        return actual != expected // intentional loose inequality to mirror Odyssey's shouldShowQuestion
      case '>':
        return bothNumeric && actualNumber > expectedNumber
      case '<':
        return bothNumeric && actualNumber < expectedNumber
      case '>=':
        return bothNumeric && actualNumber >= expectedNumber
      case '<=':
        return bothNumeric && actualNumber <= expectedNumber
      case 'includes':
        return Array.isArray(actual) && actual.includes(expected)
      case 'excludes':
        return Array.isArray(actual) && !actual.includes(expected)
      default:
        console.warn('Unsupported comparison operator in core-if condition:', operator)
        return false
    }
  }

  const evaluateStringCondition = (condition: string): boolean => {
    const match = condition.match(/^(\w+)\s*(===?|!==?|=)\s*(.+)$/)
    if (!match) {
      console.warn('Invalid core-if condition format:', condition)
      return false
    }

    const [, field, operator, rawExpectedValue] = match
    if (!field) return false

    const expectedValue = rawExpectedValue?.trim()
    const actualValue = formResults[field]
    if (actualValue === undefined) return false

    switch (operator) {
      case '=':
      case '===':
        return actualValue === expectedValue
      case '!=':
      case '!==':
        return actualValue !== expectedValue
      default:
        console.warn('Unsupported operator in core-if condition:', operator)
        return false
    }
  }

  const evaluateMultiCondition = (condition: VisibleIfMultiCondition): boolean => {
    const results = condition.conditions.map((subCondition) => evaluateCondition(subCondition))
    return condition.operator === '&&'
      ? results.every((result) => result)
      : results.some((result) => result)
  }

  const evaluateSingleCondition = (condition: VisibleIfSingleCondition): boolean => {
    const fieldValue = getNestedValue(condition.formStepKey)
    return compareValues(fieldValue, condition.comparisonOperator, condition.expectedAnswer)
  }

  const evaluateCondition = (condition: EvaluableCondition): boolean => {
    if (typeof condition === 'string') return evaluateStringCondition(condition)
    if ('conditions' in condition) return evaluateMultiCondition(condition)
    return evaluateSingleCondition(condition)
  }

  const isCoreQuestion = (coreIf: CoreIfCondition): boolean => {
    if (coreIf === false) return false
    if (coreIf === true || coreIf === undefined) return true

    try {
      return evaluateCondition(coreIf)
    } catch (error) {
      console.warn('Failed to evaluate core-if condition:', error)
      return false
    }
  }

  return {
    isCoreQuestion,
  }
}
